import React, { useState } from 'react';
import ActionableItem from 'components/common/receipt/actionable_item';
import BundleOfferCard from 'components/common/receipt/bundle_offer_card';
import { Box, Divider, Grid } from '@mui/material';
import CartBanner, { CART_BANNER_CONFIGS } from 'components/common/cart_banner';
import {
  fetchSoftwareSummaries,
  SOFTWARE_SUBSCRIPTION_STATES,
} from 'utilities/software';
import {
  BUNDLE_OFFER_RESOLUTIONS,
  BUNDLE_SQUARE_BASE_PRODUCT_KEYS,
  SOFTWARE_BASE_PRODUCT_KEYS,
} from 'constants/software';
import { onCartPageClick } from 'components/pages/cart/utilities';
import { useOrderDetails } from 'components/context/order_details';

export const SECTION_TYPES = Object.freeze({
  HARDWARE: 'hardware',
  SOFTWARE: 'software',
});

const ActionableCartSection = (props) => {
  const { trackingData } = useOrderDetails();
  const [disabled, setDisabled] = useState(false);

  const {
    orderData,
    sectionTitle,
    cartSection,
    shipQuotes,
    cartClient,
    orderRefresh,
    titleButton,
    emptyComponent,
    sectionType,
    bopisEligible,
    isPnpSubscribed,
    locationCount,
    kioskDeviceCount,
  } = props;

  const handleQtyChange = (variantToken, oldQty, newQty) => {
    const delta = newQty - oldQty;
    if (delta === 0) {
      return;
    }
    setDisabled(true);
    const action = delta > 0 ? 'add' : 'subtract';
    cartClient
      .updateQuantity(variantToken, Math.abs(delta), [], action)
      .then(orderRefresh)
      .then(() => {
        return new Promise((resolve) => {
          const targetProduct = cartSection.find(
            (item) => item.product_variant.token === variantToken,
          );

          onCartPageClick({
            actionItem: 'change_quantity',
            additionalParameters: {
              targetProduct: {
                productKey: targetProduct.product_key,
                variantToken: targetProduct.product_variant.token,
                title: targetProduct.product_variant.title,
              },
              ...trackingData,
              action,
              delta,
            },
          });
          resolve();
        });
      })
      .finally(() => setDisabled(false));
  };

  // Bundle render predicate: payload present, not suppressed, not the
  // ineligible-to-migrate special case, and a recommended tier is available.
  // Bundle UI only lives in the SOFTWARE section.
  const hardwareBundleOffer = orderData?.hardware_bundle_offer;
  const shouldRenderBundleCard =
    sectionType === SECTION_TYPES.SOFTWARE &&
    hardwareBundleOffer != null &&
    hardwareBundleOffer.resolution !== BUNDLE_OFFER_RESOLUTIONS.SUPPRESS &&
    hardwareBundleOffer.resolution !==
      BUNDLE_OFFER_RESOLUTIONS.INELIGIBLE_TO_MIGRATE &&
    hardwareBundleOffer.recommended_tier != null;

  // Filter the auto-added `sw-square-{tier}` line item out of the default
  // <ActionableItem> loop so the bundle has exactly one rendered surface (the
  // <BundleOfferCard> below). Apply only in the SOFTWARE section.
  const visibleCartSection =
    sectionType === SECTION_TYPES.SOFTWARE
      ? cartSection.filter(
          (item) =>
            !BUNDLE_SQUARE_BASE_PRODUCT_KEYS.includes(item.base_product_key),
        )
      : cartSection;

  const cartItems = visibleCartSection.reduce((acc, cartItem, index) => {
    acc.push(
      <ActionableItem
        key={`item-${cartItem.id}`}
        category={cartItem.category}
        token={cartItem.product_variant.token}
        img={cartItem.img}
        slug={cartItem.product_variant.slug}
        hasPdp={cartItem.has_pdp}
        qty={cartItem.quantity}
        limit={cartItem.max_quantity}
        title={cartItem.product_variant.title}
        price={cartItem.price}
        currency={cartItem.currency}
        outOfStock={cartItem.out_of_stock}
        disclaimer={cartItem.disclaimer}
        shipQuote={shipQuotes?.find((quote) => quote.id === cartItem.id)}
        parts={cartItem.parts}
        plan={cartItem.plan}
        softwareGroup={cartItem.software_group}
        onQtyChange={handleQtyChange}
        disabled={disabled}
        isSoftware={cartItem.is_software}
        isPnpSubscribed={isPnpSubscribed}
        locationCount={locationCount}
        kioskDeviceCount={kioskDeviceCount}
        baseProductKey={cartItem.base_product_key}
        bopisEligible={bopisEligible}
      />,
    );

    if (index !== visibleCartSection.length - 1) {
      acc.push(<Divider key={`hr-${cartItem.id}`} sx={{ my: 1 }} />);
    }

    return acc;
  }, []);

  const softwareBanners = fetchSoftwareSummaries(orderData).map((summary) => {
    if (!summary) return null;
    const { baseProductKey, softwareName, subscriptionState } = summary;

    // Kiosk P&P subscribers still pay per-device, so suppress subscription
    // banners (active, superset, and free-trial) that would confuse or block.
    if (
      isPnpSubscribed &&
      baseProductKey === SOFTWARE_BASE_PRODUCT_KEYS.SW_KIOSK
    ) {
      return null;
    }

    const config = CART_BANNER_CONFIGS[subscriptionState];

    return (
      <CartBanner
        key={softwareName}
        config={config}
        softwareName={softwareName}
      />
    );
  });

  // Short-circuit only when there's nothing to show: no visible line items,
  // no empty-state component, AND no bundle card to render.
  if (
    visibleCartSection.length === 0 &&
    !emptyComponent &&
    !shouldRenderBundleCard
  )
    return null;

  return (
    <div>
      <Grid container justifyContent="space-between" alignItems="flex-end">
        <h3>{sectionTitle}</h3>
        {titleButton && titleButton}
      </Grid>
      {sectionType === SECTION_TYPES.SOFTWARE && softwareBanners}
      <Box
        sx={{
          border: 1,
          borderColor: 'grey.300',
          borderRadius: 1.5,
          boxSizing: 'border-box',
          marginTop: 2,
        }}
      >
        {visibleCartSection.length === 0 && emptyComponent
          ? emptyComponent
          : cartItems}
        {shouldRenderBundleCard && (
          <>
            {visibleCartSection.length > 0 && <Divider sx={{ my: 1 }} />}
            <BundleOfferCard offer={hardwareBundleOffer} />
          </>
        )}
      </Box>
    </div>
  );
};

export default ActionableCartSection;
